import { useEffect, useRef, useState, type MouseEvent } from 'react';

/**
 * Coordina las DOS formas de guardar un registro "acción + texto" (Mi análisis, Veredicto):
 * el click en un botón de acción y el blur del campo de texto.
 *
 * Sin coordinación el orden de eventos rompe el guardado: mousedown en el botón → blur del
 * campo → click. El blur guardaba el texto con la acción VIEJA y deshabilitaba los botones
 * mientras viajaba, así que el click se perdía: acción pedida descartada y texto pegado a la
 * acción anterior.
 *
 * - `onMouseDownBoton` (preventDefault): el campo no pierde el foco → no hay blur → el click
 *   guarda acción + texto en UN solo pedido.
 * - Cola de pedidos en serie: si igual entran dos (teclado: Tab hasta el botón + Enter), se
 *   aplican en orden y el último manda — nunca dos requests en carrera.
 * - El blur decide contra lo ÚLTIMO PEDIDO, no contra las props (atrasadas mientras viaja el
 *   pedido anterior): no re-guarda con la acción vieja.
 * - Mientras guarda no se deshabilita nada: deshabilitar el campo le saca el foco → blur →
 *   otro pedido. El caller muestra "Guardando…" con `guardando`.
 * - El eco de CUALQUIER pedido propio (las props que el caller actualiza al resolver) no pisa
 *   el borrador: con dos pedidos en cola, el eco del primero llega cuando el "último" ya es el
 *   segundo — reconocer solo el último lo tomaba por externo y restauraba el texto viejo.
 */
export function useGuardadoCoordinado<A extends string>(p: {
  /** Acción guardada según el servidor (props). */
  accion: A | null;
  /** Texto guardado según el servidor (props). */
  texto: string;
  /** Cambia cuando el registro se actualiza desde afuera (`updated_at` / `at`). */
  version: string | null | undefined;
  guardar: (accion: A | null, texto: string) => Promise<void>;
}) {
  const [texto, setTexto] = useState(p.texto);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Pedidos emitidos cuyo eco (props actualizadas por el caller) todavía no llegó, en orden. */
  const pendientesRef = useRef<Array<{ accion: A | null; texto: string }>>([]);
  const colaRef = useRef<Promise<void>>(Promise.resolve());
  const enVueloRef = useRef(0);

  // El registro cambió: carga de la lista (externo), o el eco de un pedido propio. Los ecos
  // llegan en el orden de la cola; si coincide con el i-ésimo pendiente se consume ese y los
  // anteriores (superados) y NO se pisa lo que el usuario siguió escribiendo. Un pedido que
  // nunca coincide (el servidor normalizó el texto) queda hasta que un eco posterior lo barra.
  useEffect(() => {
    const lista = pendientesRef.current;
    const i = lista.findIndex((u) => u.accion === p.accion && u.texto === p.texto);
    if (i >= 0) pendientesRef.current = lista.slice(i + 1);
    else setTexto(p.texto);
  }, [p.version, p.texto, p.accion]);

  const ultimo = pendientesRef.current[pendientesRef.current.length - 1] ?? null;
  const accionActual = ultimo ? ultimo.accion : p.accion;
  const textoGuardado = ultimo ? ultimo.texto : p.texto;

  const pedir = (accion: A | null): Promise<void> => {
    const pedido = { accion, texto };
    pendientesRef.current = [...pendientesRef.current, pedido];
    enVueloRef.current += 1;
    setGuardando(true);
    setError(null);
    const fin = colaRef.current
      .then(() => p.guardar(pedido.accion, pedido.texto))
      .catch((e: unknown) => {
        console.error('[analisis] guardar falló:', e);
        setError(e instanceof Error ? e.message : String(e));
        // Falló: nunca va a llegar su eco — sale de la lista y las props vuelven a mandar.
        pendientesRef.current = pendientesRef.current.filter((u) => u !== pedido);
      })
      .finally(() => {
        enVueloRef.current -= 1;
        if (enVueloRef.current === 0) setGuardando(false);
      });
    colaRef.current = fin;
    return fin;
  };

  return {
    texto,
    setTexto,
    guardando,
    error,
    /** Acción vigente: la última pedida si hay una en vuelo, si no la de las props. */
    accion: accionActual,
    /** Click en un botón de acción: elegirla, o borrarla si ya estaba (toggle). */
    elegir: (a: A): Promise<void> => pedir(accionActual === a ? null : a),
    /** Blur del campo: guarda el texto con la acción vigente, solo si cambió y hay acción. */
    onBlur: (): void => {
      if (accionActual && texto !== textoGuardado) void pedir(accionActual);
    },
    /** En los botones de acción: que el campo no pierda el foco (ver arriba). */
    onMouseDownBoton: (e: MouseEvent<HTMLElement>): void => e.preventDefault(),
  };
}
